import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FilePreviewModal = ({ file, isOpen, onClose, onDownload, onDelete }) => {
  if (!isOpen || !file) return null;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPreview = () => {
    switch (file?.type) {
      case 'image':
        return (
          <div className="w-full max-h-96 bg-muted rounded-lg overflow-hidden">
            <Image
              src={file?.url}
              alt={file?.name}
              className="w-full h-full object-contain"
            />
          </div>
        );
      case 'video':
        return (
          <div className="w-full max-h-96 bg-muted rounded-lg overflow-hidden">
            <video
              controls
              className="w-full h-full"
              poster={file?.thumbnail}
            >
              <source src={file?.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        );
      default:
        return (
          <div className="w-full h-64 bg-muted rounded-lg flex items-center justify-center">
            <div className="text-center space-y-4">
              <Icon
                name="FileText"
                size={64}
                color="var(--color-muted-foreground)"
              />
              <p className="text-muted-foreground">Preview not available</p>
              <Button variant="outline" onClick={() => onDownload(file)}>
                <Icon name="Download" size={16} />
                Download to view
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Eye" size={20} color="var(--color-accent)" />
            <h2 className="text-lg font-heading text-foreground">File Preview</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Preview */}
          {renderPreview()}

          {/* File Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">File Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Name
                  </label>
                  <p className="text-sm text-foreground mt-1">{file?.name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Type
                  </label>
                  <p className="text-sm text-foreground mt-1">{file?.type?.toUpperCase()}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Size
                  </label>
                  <p className="text-sm text-foreground mt-1">{formatFileSize(file?.size)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Uploaded
                  </label>
                  <p className="text-sm text-foreground mt-1">{formatDate(file?.uploadDate)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Project & Security</h3>
              <div className="space-y-3">
                {file?.project && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Project
                    </label>
                    <p className="text-sm text-foreground mt-1">{file?.project}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Security Status
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Shield" size={16} color="var(--color-success)" />
                    <span className="text-sm text-success">Encrypted & Secure</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Access Level
                  </label>
                  <p className="text-sm text-foreground mt-1">Private</p>
                </div>
                {file?.tags && file?.tags?.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {file?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-muted text-xs text-muted-foreground rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={() => onDownload(file)}>
              <Icon name="Download" size={16} />
              Download
            </Button>
            <Button variant="outline">
              <Icon name="Share2" size={16} />
              Share
            </Button>
          </div>
          <Button
            variant="destructive"
            onClick={() => onDelete(file)}
          >
            <Icon name="Trash2" size={16} />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;